import Vue from 'vue';
import Vuex from 'vuex';
import i18n from '../i18n.js';

import APIService from '../APIService';
import moduleMap from './modules/map';
import moduleData from './modules/data';

import { findWithAttr } from './util';

Vue.use(Vuex);

const moment = require('moment');
Vue.use(require('vue-moment'), { moment });
Vue.moment.updateLocale('sl', { weekdaysShort: ['ned', 'pon', 'tor', 'sre', 'čet', 'pet', 'sob'] });

let REFRESH_INTERVAL = 5000;

export const store = new Vuex.Store({
    devicesInterval: null,
    deviceInterval: null,
    state: {
        api: new APIService(),
        moment: moment,
        i18n: i18n,
        language: localStorage.getItem('language') || 'si',
        config: {
            deviceStatusClass: ['hidden', 'has-text-grey-light', 'has-text-success', 'has-text-danger', 'has-text-warning'],
            groupNames: ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']
        },
        user: {
            username: '',
            roles: [],
            speedSettings: [30, 50, 90, 130], // TODO currently hardcoded, but can be get from the company settings API
            authenticated: false
        },
        ui: {
            ready: false,
            selectedDeviceIndex: -1,
            menuOpen: false,
            DevicePanelActive: false,
            devicePanelOpen: localStorage.getItem('devicePanelOpen') || false,
            devicePanelFullScreen: false,
            deviceDetailsPanelMinimized: false,
            toast: false,
            toastMessage: '',
            toastType: ''
        }
    },
    getters: {
        selectedDevice: state => {
            if (state.ui.selectedDeviceIndex < 0) return {};
            return state.data.devices[state.ui.selectedDeviceIndex];
        },
        language: state => {
            return state.language;
        },
        alarmActive: (state, getters) => {
            let device = getters.selectedDevice;
            const alarmContinuous = device.moveAlarmSettings.alarm.find(item => item.type === 'continuous');
            const alarmLimited = device.moveAlarmSettings.alarm.find(item => item.type === 'limited');
            return alarmContinuous.enabled || (alarmLimited.enabled &&
        Vue.moment(alarmLimited.timeLimits.from, 'HH:mm').isSameOrBefore(Vue.moment()) &&
        Vue.moment(alarmLimited.timeLimits.to, 'HH:mm').isSameOrAfter(Vue.moment()));
        },
        deviceAlarmActive: state => {
            return device => {
                const alarmContinuous = device.moveAlarmSettings.alarm.find(item => item.type === 'continuous');
                const alarmLimited = device.moveAlarmSettings.alarm.find(item => item.type === 'limited');
                return alarmContinuous.enabled || (alarmLimited.enabled &&
          Vue.moment(alarmLimited.timeLimits.from, 'HH:mm').isSameOrBefore(Vue.moment()) &&
          Vue.moment(alarmLimited.timeLimits.to, 'HH:mm').isSameOrAfter(Vue.moment()));
            };
        },
        accountLocked: state => {
            return state.user.roles.includes('ROLE_LOCKED');
        }
    },
    mutations: {
        username(state, u) {
            state.user.username = u;
        },
        roles(state, r) {
            state.user.roles = r;
        },
        authenticated(state, a) {
            state.user.authenticated = a;
        },
        ready(state, a) {
            state.ui.ready = a;
        },
        language(state, language) {
            state.language = language;
        },
        toast(state, t) {
            if (t.length < 1) {
                state.ui.toast = false;
            } else {
                state.ui.toastType = t[0];
                state.ui.toastMessage = t[1];
                state.ui.toast = true;
            }
        },
        selectedDeviceIndex(state, deviceIndex) {
            state.ui.selectedDeviceIndex = deviceIndex;
        },
        DevicePanelActive(state, bool) {
            state.ui.DevicePanelActive = bool;
        },
        menuOpen(state, value) {
            state.ui.menuOpen = value;
        },
        devicePanelOpen(state, value) {
            state.ui.devicePanelOpen = value;
        },
        deviceDetailsPanelMinimized(state, value) {
            state.ui.deviceDetailsPanelMinimized = value;
        },
        devicePanelFullScreen(state, value) {
            state.ui.devicePanelFullScreen = value;
        },
        dontRefresh(state, value) {
            state.dontRefresh = value;
        }
    },
    actions: {
        auth({ state, commit, dispatch }) {
            dispatch('data/GET_DEVICES')
                .then(() => {
                    commit('authenticated', true);
                    commit('username', localStorage.getItem('username'));
                    commit('roles', localStorage.getItem('roles'));
                    this.devicesInterval = setInterval(function() {
                        dispatch('data/GET_DEVICES');
                    }, REFRESH_INTERVAL);
                })
                .catch((error) => {
                    if (error.message === 'reauth' && state.user.authenticated) {
                        commit('authenticated', false);
                        commit('toast', ['is-danger', 'Please log in']);
                    }
                })
                .finally(() => {
                    commit('ready', true);
                });
        },
        devicePanelOpen({ state, commit, dispatch }, value) {
            commit('devicePanelOpen', value);
            if (value) {
                localStorage.setItem('devicePanelOpen', value);
            } else {
                // Close panel
                localStorage.removeItem('devicePanelOpen');

                // Reset minimized panel
                commit('deviceDetailsPanelMinimized', false);

                // Resize map
                dispatch('map/invalidateMapSize');
            }
        },
        language({ state, commit, dispatch }, language) {
            commit('language', language);
            state.i18n.locale = language;
            localStorage.setItem('language', language);
        },
        deviceDetailsPanelMinimized({ state, commit, dispatch }, minimize) {
            commit('deviceDetailsPanelMinimized', minimize);
            if (minimize) {
                dispatch('map/invalidateMapSize');
            }
        },
        doLogin({ state, commit, dispatch }, user) {
            commit('toast', []);
            return dispatch('data/LOGIN', user)
                .then(res => {
                    return dispatch('auth');
                });
        },
        logout({ commit, context }, reason) {
            commit('username', '');
            commit('authenticated', false);
            if (this.devicesInterval) clearInterval(this.devicesInterval);
            if (this.deviceInterval) clearInterval(this.deviceInterval);
            localStorage.setItem('access_token', '');
            localStorage.setItem('refresh_token', '');
            location.reload();
        },
        selectDevice({ state, commit, dispatch }, device) {
            if (device) {
                let index = findWithAttr(state.data.devices, 'id', device.id);
                dispatch('data/GET_DEVICE', device.id);

                if (this.deviceInterval) clearInterval(this.deviceInterval);
                this.deviceInterval = setInterval(function() {
                    dispatch('data/GET_DEVICE', device.id);
                }, REFRESH_INTERVAL);

                commit('selectedDeviceIndex', index);
                commit('map/followDevice', device);
                commit('DevicePanelActive', true);
            }
        }
    },
    modules: {
        map: moduleMap,
        data: moduleData
    }
});
